import { useShallow } from 'zustand/react/shallow';
import useProductDetailsStore from '../../../../../../../../stores/ProductDetails/productDetails.store';
import {
  selectHasCopiedGrid,
  selectIsCombinationMassActionSelected,
} from '../../../../../../../../stores/ProductDetails/productDetails.store.selectors';
import { MassActionCombinationItem } from '../../../../../../../../stores/ProductDetails/productDetails.types';
import { buildMassActionCombinationKey } from '../../../../../../../../stores/ProductDetails/utils/pasteGridMassAction.utils';
import { CopyOrPasteStatusEnum } from '../../../../../../../orders/products/product/components/copy-paste/copyPaste.types';
import { CombinationLineProps } from '../../../types/combinationLine.types';

interface UseCombinationLineMassActionPropsParams {
  combinationItem: MassActionCombinationItem;
  pasteStatus: CopyOrPasteStatusEnum;
  isCopiedGridLine: boolean;
  isPasteBlockedByPacks: boolean;
  disablePasteAction: boolean;
  areGridButtonsHidden: boolean;
  onClickPaste: () => void;
  onToggleSelectedItem: (item: MassActionCombinationItem) => void;
}

interface UseCombinationLineMassActionPropsOutput {
  massActionProps: CombinationLineProps['massActionProps'];
  pasteStateButtonProps: CombinationLineProps['pasteStateButtonProps'];
}

const useCombinationLineMassActionProps = ({
  combinationItem,
  pasteStatus,
  isCopiedGridLine,
  isPasteBlockedByPacks,
  disablePasteAction,
  areGridButtonsHidden,
  onClickPaste,
  onToggleSelectedItem,
}: UseCombinationLineMassActionPropsParams): UseCombinationLineMassActionPropsOutput => {
  const { copiedGridProduct, removeCopiedGrid } = useProductDetailsStore(
    useShallow((state) => ({
      copiedGridProduct: state.copiedGrid.product,
      removeCopiedGrid: state.removeCopiedGrid,
    })),
  );

  const hasCopiedGrid = useProductDetailsStore(selectHasCopiedGrid);

  const isSelected = useProductDetailsStore(
    selectIsCombinationMassActionSelected({
      combinationKey: buildMassActionCombinationKey(combinationItem),
      isPasteDisabled: disablePasteAction,
    }),
  );

  const shouldShowPasteStateButton = hasCopiedGrid && !areGridButtonsHidden;

  return {
    massActionProps: {
      isSelected,
      showCheckbox: !!copiedGridProduct,
      disableCheckbox: disablePasteAction,
      isChecked: isCopiedGridLine || isSelected,
      toggleSelectedItem: () => onToggleSelectedItem(combinationItem),
    },
    pasteStateButtonProps: shouldShowPasteStateButton
      ? {
          pasteStatus,
          isPasteBlockedByPacks,
          onClickPaste,
          onClearCopiedGrid: removeCopiedGrid,
        }
      : undefined,
  };
};

export default useCombinationLineMassActionProps;
